// REACT IMPORTS
import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { guardedNavigateGlobal } from "hooks/useNavigationGuard";

// COMPONENT IMPORTS
import {
  onSetSmallSidebar,
  useIsLogin,
  useIsSmallSidebar,
} from "store/slices/commonSlice";
import {
  adjustColorForHover,
  encode,
  onGetPortalSettings,
} from "utils/HelperFunctions";
import { useAppDispatch, useAppSelector } from "store/hooks";

// MENU IMPORTS
import { menuItems } from "./menuItems";

// ICON IMPORTS
import logo from "icons/smallLogo.svg";
import { WalletAddIcon } from "icons/sidebar/WalletAddIcon";
import MenuExpand from "icons/sidebar/MenuExpand";
import DCDialLogo from "icons/DCDialLogo";
import CustomTooltip from "components/ui-component/CustomTooltip";
import { TOKEN_STORAGE_KEY } from "store/api/axiosApi";
import { decode } from "utils/HelperFunctions";
import ProfileButton from "./ProfileButton";
import AiSidebarButton from "components/ai/AiSidebarButton";
import { useUserData } from "store/slices/authSlice";
import { RootState } from "store/store";
import { useTid } from "utils/testid";
import { getVisibleCustomerPortalMenuItems } from "utils/customerPortalPermissions";

// PROPS TYPE
interface SidebarProps {
  showWarn: boolean;
  setShowOverlay?: any;
}

/* ------------------------------------- SIDEBAR ------------------------------------- */

const Sidebar = (props: SidebarProps) => {
  const tid = useTid();
  const { showWarn } = props;
  const { accentColor } = useAppSelector((state: RootState) => state.dashboard);
  const sidebarRef = useRef<HTMLDivElement | null>(null);
  const [backgroundColor, setBackgroundColor] = useState(accentColor);
  const location = useLocation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { clusterId: cluster_id, id } = useParams();
  const clusterId = decode(cluster_id || "", true);
  const isSmallSidebar = useIsSmallSidebar();
  const [isPaymentPage, setIsPayemntPage] = useState<boolean>(false);
  const { paymentLogo: logo1 } = useAppSelector(
    (state: RootState) => state.payment
  );
  const isLogin = useIsLogin();
  const [cluster, setCluster] = useState<string>(cluster_id || "");
  const userData = useUserData();
  useEffect(() => {
    setBackgroundColor(accentColor);
  }, [accentColor]);

  useEffect(() => {
    let pathnames = location?.pathname?.split("/");
    let isSelected =
      pathnames[2] === "make-payment" ||
      pathnames[2] === "quick-checkout" ||
      pathnames[1] === "view-payment" ||
      (pathnames[1] === "agreement" && pathnames[2]) ||
      (pathnames[1] === "payment_link" && pathnames[2]) ||
      (pathnames[1] === "invoice" && pathnames[2]) ||
      (pathnames[1] === "product" && pathnames[2]) ||
      (pathnames[1] === "quotes" && pathnames[2]) ||
      pathnames[1] === "subscribe";
    if (isSelected) {
      dispatch(onSetSmallSidebar(true));
    } else {
      dispatch(onSetSmallSidebar(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.pathname]);

  useEffect(() => {
    const sidebar: any = document.getElementById("sidebar");

    if (isSmallSidebar) {
      sidebar.classList.remove("big-sidebar");
      sidebar.classList.add(
        "small-sidebar",
        "animate__animated",
        "animate__fadeInLeft"
      );
    } else {
      sidebar.classList.remove("small-sidebar");
      sidebar.classList.add(
        "big-sidebar",
        "animate__animated",
        "animate__fadeInLeft"
      );
    }
  }, [isSmallSidebar]);

  useEffect(() => {
    if (!isPaymentPage || isSmallSidebar) {
      return;
    }

    const handleOutsideClick = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      if (sidebarRef.current && !sidebarRef.current.contains(target)) {
        dispatch(onSetSmallSidebar(true));
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("touchstart", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("touchstart", handleOutsideClick);
    };
  }, [dispatch, isPaymentPage, isSmallSidebar]);

  const onGetSettings = async () => {
    try {
      let payload = {
        cluster_id:
          clusterId && clusterId?.length > 0
            ? clusterId
            : decode(id || "")?.split("_")[1],
      };
      await onGetPortalSettings(payload, dispatch);
    } catch (error) {
      console.log("error =====", error);
    }
  };

  useEffect(() => {
    onGetSettings();
  }, [isLogin]);

  const {
    accountPermission,
    accountPermissionStatus,
    accountPermissionClusterId,
  } = useAppSelector((state: RootState) => state.auth);
  const permissionClusterId =
    clusterId ||
    `${userData?.data?.accountData?.clusterid || ""}` ||
    decode(id || "")?.split("_")[1];

  const visibleMenuItems = getVisibleCustomerPortalMenuItems(menuItems, {
    accountPermission,
    accountPermissionStatus,
    accountPermissionClusterId,
    currentClusterId: permissionClusterId,
  });

  useEffect(() => {
    if (
      location?.pathname?.split("/")[2] !== "make-payment" &&
      location?.pathname?.split("/")[2] !== "quick-checkout" &&
      location?.pathname?.split("/")[1] !== "view-payment" &&
      location?.pathname?.split("/")[1] !== "agreement" &&
      location?.pathname?.split("/")[1] !== "quotes" &&
      location?.pathname?.split("/")[1] !== "invoice" &&
      location?.pathname?.split("/")[1] !== "product" &&
      location?.pathname?.split("/")[1] !== "payment_link" &&
      location?.pathname?.split("/")[1] !== "subscribe" &&
      location?.pathname?.split("/")[1] !== "invalid"
    ) {
      setIsPayemntPage(false);
    } else {
      setIsPayemntPage(true);
    }
  }, [location]);

  useEffect(() => {
    if (isPaymentPage) {
      // Logged out: quick checkout has its cluster in the address
      // (/:clusterId/quick-checkout); link pages have it inside the link code.
      setCluster(
        isLogin
          ? encode(`${userData?.data?.accountData?.clusterid}`, "ci_")
          : cluster_id ||
              (id ? encode(decode(id)?.split("_")[1], "ci_") : "")
      );
    } else {
      setCluster(
        cluster_id ||
          (isLogin && userData?.data?.accountData?.clusterid
            ? encode(`${userData?.data?.accountData?.clusterid}`, "ci_")
            : "")
      );
    }
  }, [
    isPaymentPage,
    cluster_id,
    id,
    isLogin,
    userData?.data?.accountData?.clusterid,
  ]);

  return (
    <div
      ref={sidebarRef}
      id="sidebar"
      data-testid={tid("sidebar")}
      aria-label="Primary navigation"
      className={`w-[254px] bg-bgPrimary  border-r border-strokePrimary fixed left-0 z-40 ${
        showWarn && !isPaymentPage
          ? "h-[calc(var(--shell-vh)-48px)]"
          : "h-[var(--shell-vh)]"
      } ${
        isSmallSidebar ? "w-[72px]" : "w-[254px] overflow-y-scroll classScroll"
      } `}
    >
      <div className="w-full h-full inline-flex flex-col  justify-between items-start flex-shrink-0">
        <div className="flex pt-6 flex-col items-center gap-[26px] w-full">
          <div
            className={`flex items-center gap-[10px] justify-center cursor-pointer w-full ${
              isSmallSidebar ? "px-4 h-[40px]" : "px-5 "
            }`}
          >
            {!isSmallSidebar ? (
              <div className="w-full flex gap-[10px] justify-start items-center h-[40px]">
                <span
                  role="button"
                  tabIndex={0}
                  aria-label="Collapse sidebar"
                  data-testid={tid("sidebar-collapse-button")}
                  onClick={() => dispatch(onSetSmallSidebar(!isSmallSidebar))}
                >
                  <MenuExpand aria-hidden="true" />
                </span>
                <span
                  className={`flex justify-center items-center h-10 ${!isSmallSidebar ? "animate__animated animate__fadeIn" : "animate__animated animate__fadeOut"}`}
                  role="button"
                  tabIndex={0}
                  aria-label="Go to dashboard"
                  data-testid={tid("sidebar-brand-link")}
                  onClick={() =>
                    guardedNavigateGlobal(navigate, `/${cluster_id}/dashboard`)
                  }
                >
                  {location?.pathname?.split("/")[1] === "subscribe" ? (
                    <DCDialLogo aria-hidden="true" />
                  ) : logo1 ? (
                    <span className="h-10 flex">
                      <img
                        src={logo1}
                        alt="Company logo"
                        className="object-contain"
                      />
                    </span>
                  ) : (
                    <span className="h-10 flex items-center justify-center gap-2">
                      <img
                        src={logo}
                        alt="DCDial logo"
                        className="object-contain"
                      />
                      <div className="text-purple font-medium text-xl leading-[30px]">
                        CompanyName
                      </div>
                    </span>
                  )}
                </span>
              </div>
            ) : (
              <span
                role="button"
                tabIndex={0}
                aria-label="Expand sidebar"
                data-testid={tid("sidebar-expand-button")}
                onClick={() => dispatch(onSetSmallSidebar(!isSmallSidebar))}
              >
                <MenuExpand aria-hidden="true" />
              </span>
            )}
          </div>
          <div className="max-w-[223px] w-full h-[1px] bg-strokePrimary" />
          <div className="w-full pb-3 flex items-center gap-6 flex-col">
            <div className="flex items-center gap-6 flex-col w-full ">
              <div className="flex flex-col items-center gap-6 w-full">
                {visibleMenuItems.map((val, i: number) => {
                  let pathnames = location?.pathname?.split("/");
                  let isSelected =
                    `/${pathnames[2]}` === val?.url ||
                    (`/${pathnames[0]}` === location?.pathname &&
                      val?.id === "dashboard");
                  const Icon = val?.icon;
                  let clusterId = cluster_id
                    ? cluster_id
                    : userData?.data?.accountData?.clusterid
                      ? encode(
                          `${userData?.data?.accountData?.clusterid}`,
                          "ci_"
                        )
                      : id
                        ? encode(decode(id || "")?.split("_")[1], "ci_")
                        : "";
                  return (
                    <div
                      role="link"
                      tabIndex={0}
                      aria-current={isSelected ? "page" : undefined}
                      aria-label={val.title}
                      data-testid={tid(`sidebar-${val.id}-link`)}
                      onClick={() => {
                        if (localStorage?.getItem(TOKEN_STORAGE_KEY)) {
                          guardedNavigateGlobal(
                            navigate,
                            `/${clusterId}${val.url}`
                          );
                        } else {
                          guardedNavigateGlobal(
                            navigate,
                            `/${clusterId}/login`
                          );
                        }
                      }}
                      key={val.id}
                      className="pr-[14px] pl-[17px] w-full cursor-pointer relative"
                    >
                      {isSelected ? (
                        <div className="h-10 w-[3px] bg-blue absolute left-0 rounded-tr-[6px] rounded-br-[6px] animate__animated animate__fadeIn"></div>
                      ) : null}

                      {isSmallSidebar ? (
                        <CustomTooltip
                          maxwidth="430px"
                          place="right"
                          id={val.title}
                          content={`<div class="text-sm leading-[18px] font-normal ${val?.className}">${val.title}</div>`}
                        >
                          <div
                            className={`flex items-center gap-4 text-base font-medium leading-6 p-[7px] rounded-[5px] hover:!bg-bgSecondary ${
                              isSelected
                                ? "rounded-[5px] bg-bgSecondary text-blue animate__animated animate__fadeIn"
                                : "text-txtSecondary"
                            }`}
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();

                              if (localStorage?.getItem(TOKEN_STORAGE_KEY)) {
                                guardedNavigateGlobal(
                                  navigate,
                                  `/${clusterId}${val.url}`
                                );
                              } else {
                                guardedNavigateGlobal(
                                  navigate,
                                  `/${clusterId}/login`
                                );
                              }
                            }}
                          >
                            <Icon
                              aria-hidden="true"
                              id={val.title}
                              strokeColor={
                                isSelected ? "var(--color-blue)" : undefined
                              }
                            />
                          </div>
                        </CustomTooltip>
                      ) : (
                        <div
                          className={`flex items-center gap-4 text-base font-medium leading-6 p-[7px] rounded-[5px] hover:!bg-bgSecondary ${
                            isSelected
                              ? "rounded-[5px] bg-bgSecondary text-blue animate__animated animate__fadeIn"
                              : "text-txtSecondary"
                          }`}
                        >
                          <Icon
                            aria-hidden="true"
                            strokeColor={
                              isSelected ? "var(--color-blue)" : undefined
                            }
                          />
                          <div
                            className={`${
                              !isSmallSidebar
                                ? "animate__animated animate__fadeIn"
                                : "animate__animated animate__fadeOut"
                            }`}
                          >
                            {val.title}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div
                className={`w-full h-[1px] bg-strokePrimary ${
                  isSmallSidebar ? "max-w-[50px]" : "max-w-[223px]"
                }`}
              />
            </div>
            <Link
              to={`/${cluster}/make-payment`}
              data-testid={tid("sidebar-make-payment-link")}
              aria-label="Make payment"
              style={{
                backgroundColor:
                  location?.pathname?.split("/")[2] === "make-payment"
                    ? adjustColorForHover(accentColor)
                    : backgroundColor,
              }}
              className={`${
                isSmallSidebar ? "w-[38px] h-[38px] !p-[7px]" : "w-[222px]"
              } px-4 py-[10px] rounded-md text-base text-white leading-6 font-medium focus:outline-none flex items-center gap-2 justify-center`}
              onMouseEnter={(e: any) => {
                setBackgroundColor(adjustColorForHover(accentColor));
              }}
              onMouseLeave={(e: any) => {
                setBackgroundColor(accentColor);
              }}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();

                if (localStorage?.getItem(TOKEN_STORAGE_KEY)) {
                  guardedNavigateGlobal(navigate, `/${cluster}/make-payment`);
                } else {
                  guardedNavigateGlobal(navigate, `/${cluster}/login`);
                }
              }}
            >
              {isSmallSidebar ? (
                <CustomTooltip
                  maxwidth="430px"
                  place="right"
                  id="make-payment-icon"
                  content={`<div class="text-sm leading-[18px] font-normal w-max">Make Payment</div>`}
                  tooltipClassName="!translate-x-[67px] !translate-y-[527px]"
                >
                  <span
                    id="make-payment-icon"
                    data-testid={tid("sidebar-make-payment-icon")}
                    aria-label="Make payment"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();

                      if (localStorage?.getItem(TOKEN_STORAGE_KEY)) {
                        guardedNavigateGlobal(
                          navigate,
                          `/${cluster}/make-payment`
                        );
                      } else {
                        guardedNavigateGlobal(navigate, `/${cluster}/login`);
                      }
                    }}
                  >
                    <WalletAddIcon aria-hidden="true" />
                  </span>
                </CustomTooltip>
              ) : (
                "Make Payment"
              )}
            </Link>
          </div>
        </div>
        <div className="w-full flex flex-col gap-2">
          {/* Renders nothing unless the customer is signed in and the AI
              service accepts their token. */}
          <AiSidebarButton />
          <ProfileButton cluster={cluster} />
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
